import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { initials, usePresence } from '../lib/presence';
import { MODULES } from '../lib/access';

const pageName = (path: string) => (path === '/' ? 'Action Centre' : MODULES.find((m) => m.path && m.path !== '/' && path.startsWith(m.path))?.label ?? path);

/** Top-bar: faces of the people online on this site, with a list of where they are. */
export function OnlineButton() {
  const { others } = usePresence();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const active = others.filter((o) => o.status === 'active');
  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="online-btn"
        aria-label={`${others.length} other ${others.length === 1 ? 'person' : 'people'} online`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="faces">
          {others.slice(0, 3).map((o) => (
            <span key={o.uid} className={`face ${o.status}`} title={o.name}>
              {initials(o.name)}
            </span>
          ))}
        </span>
        <span className="online-count">
          <span className={`dot ${active.length ? 'on' : ''}`} />
          {others.length ? `${others.length} online` : 'Only you'}
        </span>
      </button>
      {open ? (
        <div className="user-pop" style={{ width: 280 }}>
          <strong>On this site now</strong>
          {others.length === 0 ? <span className="muted small">Nobody else is signed in on this site.</span> : null}
          {others.map((o) => (
            <div key={o.uid} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span className={`face ${o.status}`}>{initials(o.name)}</span>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <strong className="small">{o.name}</strong>
                <span className="muted small">
                  {o.status === 'away' ? 'Away · ' : ''}
                  {pageName(o.path)}
                  {o.record ? ' · has a record open' : ''}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Thin bar at the top of a page: "Sean is also on this page". */
export function AlsoHere() {
  const loc = useLocation();
  const { onPage } = usePresence();
  const here = onPage(loc.pathname);
  if (!here.length) return null;
  const names = here.map((h) => h.name.split(' ')[0]);
  return (
    <div className="also-here" role="status">
      <span className="faces">
        {here.slice(0, 4).map((h) => (
          <span key={h.uid} className={`face ${h.status}`}>
            {initials(h.name)}
          </span>
        ))}
      </span>
      <span>
        {names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names.at(-1)} {names.length === 1 ? 'is' : 'are'} also on this page — changes show up
        here automatically.
      </span>
    </div>
  );
}
